import React, { useState } from 'react';
import { FormattedNumber, FormattedMessage } from 'react-intl';

import InvoiceBreakdownSection from 'components/Core/Invoice/BreakdownSection/InvoiceBreakdownSection';
import DataRow from 'components/Shared/DataRow/DataRow';
import Hr from 'components/Shared/Hr/Hr';
import InvoiceStatus from 'components/Shared/InvoiceStatus/InvoiceStatus';
import CpPopover from 'components/Shared/Popover/CpPopover';
import { useIsMobileScreen } from 'hooks/useIsMobile';
import { useRHSExperiment } from 'hooks/useRHSExperiment';
import SegmentIO from 'reporting/SegmentIO';
import { saleSelectors } from 'store/sale/selectors';
import { useSelector } from 'store/utils';
import { colors, fontSize } from 'styles/cp';
import { LineItem } from 'types/Sale';

export interface InvoiceDataRowsProps {
  currency?: string;
  isPartiallyPaid?: boolean;
  isFullyPaid?: boolean;
  invoiceAmount?: number;
  achOnlineConvenienceFeeAmount?: number;
  achOnlineConvenienceFeeAmountPaid?: number;
  feeAmount?: number;
  ccSurchargingFeeAmount?: number;
  ccSurchargingFeePercentage?: string;
  // Breakdown experiment props
  isBreakdownExpanded?: boolean;
  onToggleBreakdown?: () => void;
  // Line items data for breakdown treatment
  invoiceLineItems?: LineItem[];
  subTotalAmount?: number;
  discountAmount?: number;
  shippingAmount?: number;
  invoiceTaxAmount?: number;
  hrColor?: string;
}

const InvoiceDataRows = ({
  currency,
  isPartiallyPaid,
  isFullyPaid,
  invoiceAmount,
  achOnlineConvenienceFeeAmount = 0,
  achOnlineConvenienceFeeAmountPaid = 0,
  ccSurchargingFeeAmount,
  ccSurchargingFeePercentage,
  // Breakdown experiment props
  isBreakdownExpanded,
  onToggleBreakdown,
  // Line items data for breakdown treatment
  invoiceLineItems,
  subTotalAmount,
  discountAmount,
  shippingAmount,
  invoiceTaxAmount,
  hrColor,
}: InvoiceDataRowsProps) => {
  const { shouldShowConvenienceFeeTotal, shouldShowBreakdownSection } = useRHSExperiment();
  const isMobile = useIsMobileScreen(768);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [hasTooltipBeenHoveredOnce, setHasTooltipBeenHoveredOnce] = useState(false);
  const onMouseEnter = () => {
    setIsTooltipOpen(true);
    !hasTooltipBeenHoveredOnce &&
      SegmentIO.transactionEngaged({
        ui_object: 'tooltip',
        ui_action: 'hovered',
        ui_object_detail: 'convenience_fee_tooltip',
        ui_access_point: 'transaction_flow',
      });
    setHasTooltipBeenHoveredOnce(true);
  };
  let feeAmount = 0;
  const shouldShowCCConvenienceFee =
    typeof ccSurchargingFeeAmount === 'number' && typeof ccSurchargingFeePercentage === 'string';
  const isACHConvenienceFeeEnabled = useSelector((state) =>
    saleSelectors.achOnlineConvenienceFeeEnabledSelector(state.sale)
  );
  if (isFullyPaid) {
    if (achOnlineConvenienceFeeAmountPaid) {
      feeAmount = Number(achOnlineConvenienceFeeAmountPaid);
    }
  } else {
    // Prefer CC surcharge amount when provided; fallback to ACH amounts
    if (shouldShowCCConvenienceFee) {
      feeAmount = ccSurchargingFeeAmount;
    } else {
      feeAmount = Number(achOnlineConvenienceFeeAmount);
    }
  }
  // for fully paid
  const totalAmount = invoiceAmount && feeAmount ? invoiceAmount + feeAmount : feeAmount;

  const shouldDisplayConvenienceFeeTotal = shouldShowConvenienceFeeTotal({
    isBreakdownExpanded: !!isBreakdownExpanded,
  });

  const shouldRenderBreakdownSection = shouldShowBreakdownSection({
    onToggleBreakdown,
  });
  return (
    <>
      {isPartiallyPaid && <InvoiceStatus idValue="INVOICE_SUMMARY_PARTIALLY_PAID" />}
      {((feeAmount > 0 && isACHConvenienceFeeEnabled) || shouldShowCCConvenienceFee) && (
        <>
          <span>
            <li aria-label="data row item" id="maip-tooltip-w">
              <div className="space-between flex-row">
                <span
                  className="label left"
                  onMouseEnter={onMouseEnter}
                  onTouchStart={onMouseEnter}
                  onMouseLeave={() => setIsTooltipOpen(false)}
                >
                  {shouldShowCCConvenienceFee ? (
                    <span className="borderBottom">
                      <FormattedMessage
                        id="CC_SURCHARGE_ONLINE_CONVENIENCE_FEE"
                        defaultMessage="`Credit card surcharge ({feePercentage})`"
                        values={{ feePercentage: `${ccSurchargingFeePercentage}%` }}
                      />
                    </span>
                  ) : (
                    <span className="borderBottom">
                      <FormattedMessage
                        id="MAIP_ONLINE_CONVENIENCE_FEE"
                        defaultMessage="Online convenience fee"
                      />
                    </span>
                  )}
                </span>
                <span className="label right">
                  <FormattedNumber value={feeAmount} style="currency" currency={currency} />
                </span>
              </div>
            </li>
          </span>
          {shouldDisplayConvenienceFeeTotal && (
            <>
              <Hr marginTop={10} marginBottom={0} borderColor={colors.offWhite} opacity={1} />
              <DataRow
                idValue="ACH_ONLINE_CONVENIENCE_FEE_INVOICE_TOTAL"
                defaultMessage="Total"
                bold={true}
                color={colors.black}
                value={<FormattedNumber value={totalAmount} style="currency" currency={currency} />}
              />
              <Hr marginTop={10} marginBottom={0} borderColor={colors.offWhite} opacity={1} />
            </>
          )}
        </>
      )}

      {/* Growth RHS - Invoice Breakdown experiment section */}
      {shouldRenderBreakdownSection && (
        <InvoiceBreakdownSection
          isExpanded={!!isBreakdownExpanded}
          onToggle={onToggleBreakdown!}
          invoiceAmount={invoiceAmount || 0}
          currency={currency}
          invoiceLineItems={invoiceLineItems}
          subTotalAmount={subTotalAmount}
          discountAmount={discountAmount}
          shippingAmount={shippingAmount}
          invoiceTaxAmount={invoiceTaxAmount}
          hrColor={hrColor}
        />
      )}
      <CpPopover
        className="cp-tooltip-wrapper"
        trigger="hover"
        innerClassName="cp-tooltip"
        placement={isMobile ? 'bottom' : 'top-end'}
        target={isMobile ? 'invoice-more-info' : 'maip-tooltip-w'}
        isOpen={isTooltipOpen}
      >
        {shouldShowCCConvenienceFee ? (
          <FormattedMessage
            id="CC_SURCHARGE_TOOLTIP_MSG"
            defaultMessage="This fee helps businesses cover credit card processing costs."
          />
        ) : (
          <FormattedMessage
            id="MAIP_TOOLTIP_MSG"
            defaultMessage="This is a fee for paying this invoice online. You won't be charged this fee if you pay another way."
          />
        )}
      </CpPopover>
      <style jsx>{`
        .space-between {
          display: flex;
          justify-content: space-between;
        }

        .flex-row {
          flex-direction: row;
          margin: 6px 0px;

          .label {
            cursor: text;
            font-size: ${fontSize.xs};
            color: ${colors.gray};
            display: flex;
            font-family: AvenirNextforINTUIT-Regular;
          }

          .right {
            text-align: right;
            font-family: AvenirNextforINTUIT-Medium;
            margin-left: 5px;
          }

          .left {
            text-align: left;
            border-bottom: dotted;
            border-width: 1px;
            cursor: pointer;
          }
        }

        li {
          display: block;
        }
      `}</style>
    </>
  );
};

export default InvoiceDataRows;
